import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './StudentAchievements.css';

const STEP = 12;

// "Karim Haddad" -> "Karim H." (children's privacy on a public page)
const displayName = (first = '', last = '') => {
  const f = String(first || '').trim();
  const l = String(last || '').trim();
  return l ? `${f} ${l.charAt(0).toUpperCase()}.` : f;
};

const formatMonth = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const StudentAchievements = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [course, setCourse] = useState('All');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(STEP);

  useEffect(() => {
    let cancelled = false;
    const fetchAchievements = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/achievements`);
        const data = Array.isArray(response.data) ? response.data : [];
        const mapped = data
          .map((cert, index) => ({
            id: cert.Id ?? cert.id ?? index,
            student: displayName(cert.FirstName, cert.LastName),
            course: cert.ModuleName || 'Course',
            date: cert.Date,
            time: new Date(cert.Date).getTime() || 0,
          }))
          .sort((a, b) => b.time - a.time); // newest first
        if (!cancelled) {
          setItems(mapped);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    };
    fetchAchievements();
    return () => {
      cancelled = true;
    };
  }, []);

  const courses = useMemo(() => ['All', ...[...new Set(items.map((i) => i.course))].sort()], [items]);
  const courseCount = courses.length - 1;

  const filtered = items.filter((i) => {
    const matchesCourse = course === 'All' || i.course === course;
    const q = query.trim().toLowerCase();
    const matchesQuery = !q || i.student.toLowerCase().includes(q) || i.course.toLowerCase().includes(q);
    return matchesCourse && matchesQuery;
  });

  return (
    <section className="rmach" aria-labelledby="rmach-title">
      <div className="rmach-inner">
        <header className="rmach-header">
          <span className="rmach-eyebrow">Student achievements</span>
          <h1 id="rmach-title" className="rmach-title">
            Every certificate is a <span>finished course</span>
          </h1>
          <p className="rmach-sub">
            Each one was earned by completing a full course, live and 1-to-1, with real projects along the way.
          </p>

          {status === 'ready' && items.length > 0 && (
            <div className="rmach-stats">
              <div>
                <strong>{items.length.toLocaleString('en-US')}</strong>
                <span>certificates earned</span>
              </div>
              <div>
                <strong>{courseCount}</strong>
                <span>different courses</span>
              </div>
            </div>
          )}
        </header>

        {status === 'loading' && (
          <div className="rmach-grid" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rmach-card rmach-skeleton" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="rmach-empty">
            <h2>Achievements couldn’t load right now</h2>
            <p>Please try again in a moment.</p>
          </div>
        )}

        {status === 'ready' && (
          <>
            <div className="rmach-controls">
              <div className="rmach-search">
                <FontAwesomeIcon icon={faSearch} className="rmach-search-icon" aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Search by student or course"
                  placeholder="Search by first name or course"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setVisible(STEP);
                  }}
                />
              </div>
              <label className="rmach-select">
                <span>Course</span>
                <select
                  value={course}
                  onChange={(e) => {
                    setCourse(e.target.value);
                    setVisible(STEP);
                  }}
                >
                  {courses.map((c) => (
                    <option key={c} value={c}>
                      {c === 'All' ? 'All courses' : c}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {filtered.length === 0 ? (
              <div className="rmach-empty">
                <p>No certificates found{query ? ` for “${query}”` : ''}.</p>
                <button
                  type="button"
                  className="rmach-more"
                  onClick={() => {
                    setQuery('');
                    setCourse('All');
                  }}
                >
                  Show all certificates
                </button>
              </div>
            ) : (
              <>
                <p className="rmach-count">
                  {filtered.length} certificate{filtered.length === 1 ? '' : 's'}
                </p>
                <ul className="rmach-grid">
                  {filtered.slice(0, visible).map((i) => (
                    <li key={i.id} className="rmach-card">
                      <span className="rmach-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faAward} />
                      </span>
                      <div className="rmach-card-body">
                        <span className="rmach-course">{i.course}</span>
                        <span className="rmach-student">{i.student}</span>
                      </div>
                      <span className="rmach-date">{formatMonth(i.date)}</span>
                    </li>
                  ))}
                </ul>
                {visible < filtered.length && (
                  <button type="button" className="rmach-more" onClick={() => setVisible((v) => v + STEP)}>
                    Show more ({filtered.length - visible})
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default StudentAchievements;